/**
 * cancellation-events — tradução pt-BR e agrupamento do catálogo de eventos
 * da aba Cancelamento (spec 2026-09-14 §1.2).
 *
 * `WORKFLOW_CANCELLATION_EVENTS` (`@troia-v3/types`) é a fonte única do
 * catálogo (evento → entidade/idField/actors); este arquivo só adiciona o
 * que é da UI: rótulo em português, agrupamento por família e a descrição
 * de `WorkflowCancelReason` mostrada no histórico de execuções.
 */
import type { WorkflowCancelReason, WorkflowCancellationEventType, WorkflowEventActor } from '@troia-v3/types';
import { WORKFLOW_CANCELLATION_EVENTS } from '@troia-v3/types';

export const CANCELLATION_EVENT_LABELS: Record<WorkflowCancellationEventType, string> = {
  'message.received': 'Mensagem recebida',
  'message.sent': 'Mensagem enviada',
  'conversation.created': 'Conversa criada',
  'conversation.updated': 'Conversa atualizada',
  'conversation.closed': 'Conversa encerrada',
  'conversation.assigned': 'Conversa atribuída',
  'contact.created': 'Contato criado',
  'contact.updated': 'Contato atualizado',
  'lead.created': 'Lead criado',
  'lead.updated': 'Lead atualizado',
  'lead.stage_changed': 'Lead mudou de etapa',
  'lead.won': 'Lead ganho',
  'lead.lost': 'Lead perdido',
  'ticket.created': 'Ticket criado',
  'ticket.updated': 'Ticket atualizado',
  'ticket.status_changed': 'Ticket mudou de status',
  'ticket.assigned': 'Ticket atribuído',
  'ticket.resolved': 'Ticket resolvido',
  'ticket.closed': 'Ticket fechado',
  'calendar_event.created': 'Compromisso criado',
  'calendar_event.updated': 'Compromisso atualizado',
  'calendar_event.cancelled': 'Compromisso removido',
};

export const CANCELLATION_EVENT_GROUPS: ReadonlyArray<{ label: string; events: WorkflowCancellationEventType[] }> = [
  { label: 'Mensagem', events: ['message.received', 'message.sent'] },
  { label: 'Conversa', events: ['conversation.created', 'conversation.updated', 'conversation.closed', 'conversation.assigned'] },
  { label: 'Lead', events: ['lead.created', 'lead.updated', 'lead.stage_changed', 'lead.won', 'lead.lost'] },
  { label: 'Ticket', events: ['ticket.created', 'ticket.updated', 'ticket.status_changed', 'ticket.assigned', 'ticket.resolved', 'ticket.closed'] },
  { label: 'Contato', events: ['contact.created', 'contact.updated'] },
  { label: 'Agenda', events: ['calendar_event.created', 'calendar_event.updated', 'calendar_event.cancelled'] },
];

/** Mesmos rótulos que o Aguardar antigo usava (WaitForConfig.tsx:69-74). */
export const ACTOR_LABELS: Record<WorkflowEventActor, string> = { user: 'Atendente', ai: 'Agente de IA', automation: 'Automação', api: 'API' };

const ENTITY_LABELS: Record<string, string> = { conversation: 'a conversa', contact: 'o contato', lead: 'o lead', ticket: 'o ticket', event: 'o compromisso' };

export function defaultTargetLabel(eventType: WorkflowCancellationEventType): string {
  return ENTITY_LABELS[WORKFLOW_CANCELLATION_EVENTS[eventType].entity] ?? 'a entidade';
}

export function describeCancellation(reason: WorkflowCancelReason): string {
  if (reason.kind === 'user') return 'Cancelado por: usuário';
  const label = reason.eventType !== undefined ? (CANCELLATION_EVENT_LABELS[reason.eventType as WorkflowCancellationEventType] ?? reason.eventType) : 'evento';
  const node = reason.nodeLabel !== undefined ? ` (regra do nó "${reason.nodeLabel}")` : '';
  return `Cancelado por: ${label}${node}`;
}
